import React, { useCallback, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ActivityIndicator,
  TouchableOpacity,
} from 'react-native';
import { StatusBar } from 'expo-status-bar';
import LeaderboardList from '@/components/LeaderboardList';
import { useRouter, useFocusEffect } from 'expo-router';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { fetchLeaderboardGymV1, type LeaderboardRowV1 } from '@/lib/leaderboard';
import { supabase } from '@/lib/supabase';
import { fetchBaselineStatus } from '@/lib/baselineClient';
import type { BaselineStatus } from '@/lib/baselineGate';
import BaselineGateOverlay from '@/components/BaselineGateOverlay';

export default function GymLeaderboardScreen() {
  const router = useRouter();
  const { profile: currentUserProfile, refreshProfile } = useAuth();
  const { theme } = useTheme();
  const [rows, setRows] = useState<LeaderboardRowV1[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [baselineStatus, setBaselineStatus] = useState<BaselineStatus | null>(null);

  const gymId = currentUserProfile?.gym_id;

  const load = async () => {
    if (!gymId) return;
    setError(null);
    setLoading(true);
    const result = await fetchLeaderboardGymV1(supabase, { gymId });
    setRows(result.rows);
    setError(result.error);
    setLoading(false);
  };

  useFocusEffect(
    useCallback(() => {
      (async () => {
        if (refreshProfile) await refreshProfile();
        await load();
        // Fetch baseline status for gate overlay
        if (currentUserProfile?.id) {
          try {
            const bs = await fetchBaselineStatus(currentUserProfile.id);
            setBaselineStatus(bs);
          } catch { /* non-critical */ }
        }
      })();
    }, [gymId])
  );

  const c = theme.colors;

  if (!gymId) {
    return (
      <View style={[styles.center, { backgroundColor: c.background }]}>
        <TouchableOpacity style={[styles.backButton, { borderColor: c.border }]} onPress={() => router.push('/(tabs)')}>
          <Text style={[styles.backButtonText, { color: c.text }]}>Back to App</Text>
        </TouchableOpacity>
        <Text style={[styles.emptyTitle, { color: c.text }]}>No gym selected</Text>
        <Text style={[styles.emptyText, { color: c.textSecondary }]}>
          Set a home gym to view the gym leaderboard.
        </Text>
        <TouchableOpacity style={[styles.primaryButton, { backgroundColor: c.surfaceVariant }]} onPress={() => router.push('/(profile)/home-gym')}>
          <Text style={[styles.primaryButtonText, { color: c.text }]}>Set Home Gym</Text>
        </TouchableOpacity>
        <TouchableOpacity style={[styles.secondaryButton, { borderColor: c.border }]} onPress={() => router.push('/join-gym')}>
          <Text style={[styles.secondaryButtonText, { color: c.text }]}>Join a Gym</Text>
        </TouchableOpacity>
      </View>
    );
  }

  // Baseline gate: show overlay if baseline incomplete
  if (baselineStatus && !baselineStatus.canAppearOnLeaderboard) {
    return <BaselineGateOverlay status={baselineStatus} feature="leaderboard" />;
  }

  return (
    <View style={[styles.container, { backgroundColor: c.background }]}>
      <StatusBar style="light" />
      <View style={styles.topBar}>
        <TouchableOpacity style={[styles.backButton, { borderColor: c.border }]} onPress={() => router.push('/(tabs)')}>
          <Text style={[styles.backButtonText, { color: c.text }]}>Back to App</Text>
        </TouchableOpacity>
      </View>

      {loading ? (
        <View style={styles.center}>
          <ActivityIndicator size="large" color={c.accent} />
          <Text style={[styles.subtle, { color: c.textTertiary }]}>Loading leaderboard...</Text>
        </View>
      ) : error ? (
        <View style={styles.center}>
          <Text style={[styles.errorText, { color: c.textSecondary }]}>Error: {error}</Text>
          <TouchableOpacity style={[styles.retryButton, { backgroundColor: c.surfaceVariant }]} onPress={load}>
            <Text style={[styles.retryText, { color: c.text }]}>Retry</Text>
          </TouchableOpacity>
        </View>
      ) : rows.length === 0 ? (
        <View style={styles.center}>
          <Text style={[styles.emptyText, { color: c.textSecondary }]}>No ranked members yet</Text>
        </View>
      ) : (
        <LeaderboardList
          rows={rows}
          onPressRow={(row) => {
            if (!row.handle) return;
            router.push({ pathname: '/u/[handle]' as any, params: { handle: row.handle } });
          }}
        />
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1 },
  center: { flex: 1, justifyContent: 'center', alignItems: 'center', paddingHorizontal: 24 },
  emptyTitle: { fontSize: 18, fontWeight: '800', marginBottom: 8 },
  primaryButton: { paddingVertical: 12, paddingHorizontal: 24, borderRadius: 2, marginBottom: 10 },
  primaryButtonText: { fontWeight: '700' },
  secondaryButton: { borderWidth: 1, paddingVertical: 10, paddingHorizontal: 24, borderRadius: 2 },
  secondaryButtonText: { fontWeight: '700' },
  subtle: { marginTop: 8 },
  errorText: {},
  retryButton: { marginTop: 12, padding: 10, borderRadius: 2 },
  retryText: { fontWeight: '700' },
  emptyText: { textAlign: 'center', marginBottom: 16, paddingHorizontal: 24 },
  topBar: { paddingHorizontal: 16, paddingTop: 12, paddingBottom: 4 },
  backButton: { alignSelf: 'flex-start', paddingVertical: 6, paddingHorizontal: 12, borderRadius: 2, borderWidth: 1 },
  backButtonText: { fontWeight: '700' },
});
